import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import {
  Card,
  ProfileMapDisplay,
  SearchResultContactLink,
  SchoolYearNav,
  ExternalLink,
  ActiveBadges,
} from 'components'
import * as actions from '../actions'
import * as apiActions from 'api-actions'
import * as routerActions from 'react-router-redux'
import { connectQuery } from 'lp-hoc'
import { selectors as apiSelectors } from 'lp-redux-api'
import { Element } from 'react-scroll'
import {
  LocationIcon,
  PhoneIcon,
  DesktopIcon,
  PaletteIcon,
  CalendarIcon,
  WalletIcon,
  TextIcon,
  InstagramIcon,
} from 'components/icons'
import imagePlaceholder from 'images/image-placeholder.jpeg'
import { first, get, size, take, some } from 'lodash'
import { Link } from 'react-router'
import { Spinner } from 'lp-components'
import { ArtistContent } from '../components'
import { selectors } from '../reducer'
import { selectors as globalSelectors } from 'global-reducer'
import * as Types from 'types'
import { useCommunity } from 'utils'

const propTypes = {
  artist: Types.artist,
  isLoading: PropTypes.bool.isRequired,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  programTypes: PropTypes.arrayOf(Types.programType).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.outcomeType).isRequired,
  approaches: PropTypes.arrayOf(Types.approach).isRequired,
  languages: PropTypes.arrayOf(Types.language).isRequired,
  organizationLocations: PropTypes.arrayOf(Types.location).isRequired,
  artistLocation: Types.location,
  schoolYears: PropTypes.arrayOf(Types.schoolYear),
  year: PropTypes.number,
  fetchArtist: PropTypes.func.isRequired,
  clearArtist: PropTypes.func.isRequired,
  fetchActiveAndPublishedSchoolYears: PropTypes.func.isRequired,
  redirectToNotFound: PropTypes.func.isRequired,
}

const defaultPropTypes = {
  artist: null,
  artistLocation: null,
  schoolYears: null,
}

function formatMostRecentLogin(dateString) {
  if (!dateString) return null
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

function TeachingArtist({
  isLoading,
  artist,
  organizationLocations,
  artistLocation,
  schoolYears,
  year,
  disciplines,
  programTypes,
  outcomeTypes,
  approaches,
  languages,
  fetchActiveAndPublishedSchoolYears,
  fetchArtist,
  clearArtist,
  redirectToNotFound,
}) {
  const community = useCommunity()
  const profile = get(artist, 'profile', {})
  const showPhoneOnMap = get(artist, 'showPhoneOnMap', false)

  const selectedSchoolYearId = year || get(first(schoolYears), 'id')

  useEffect(() => {
    fetchActiveAndPublishedSchoolYears({ recent: false })
  }, [])

  useEffect(() => {
    if (!selectedSchoolYearId) return
    fetchArtist(selectedSchoolYearId).catch((e) => {
      if (e && e.status === 404) redirectToNotFound()
    })
  }, [selectedSchoolYearId])

  useEffect(() => clearArtist, [])

  useEffect(() => {
    if (!schoolYears) return
    if (!some(schoolYears, ({ id }) => id === selectedSchoolYearId))
      redirectToNotFound()
  }, [schoolYears, selectedSchoolYearId])

  if (!schoolYears) return <Spinner />
  const numSchoolYears = size(schoolYears)
  const showMostRecentLogin = get(community, 'showRecentLoginOnPublicPage')
  const mostRecentLoginLabel = formatMostRecentLogin(
    get(artist, 'mostRecentLoginAt')
  )

  return (
    <div className="content teaching-artist-page">
      {/* Header */}
      <header className="profile-details-block grey-background">
        <div className="map-page-header interior-page" />
        <Element
          className="header-block content-block-container"
          name="details"
        >
          {!artist && <div className="header-padding" />}
          {artist && (
            <div>
              {numSchoolYears !== 0 && (
                <SchoolYearNav
                  baseUrl={`/teaching-artist/${artist.id}`}
                  message="Showing Teaching Artist school year data for:"
                  schoolYears={take(schoolYears, 5)}
                  selectedSchoolYearId={selectedSchoolYearId}
                />
              )}
              <Card>
                <div className="show-header">
                  <div className="text-block">
                    <div className="image-preview">
                      <div className="image-thumbnail">
                        <img
                          src={artist.profilePic || imagePlaceholder}
                          alt={`Profile photo of ${artist.name}`}
                        />
                      </div>
                      <div className="artist-header text-block">
                        <div className="artist-name">
                          <h1>{artist.name}</h1>
                          {profile.pronouns && (
                            <span className="artist-pronouns">
                              ({profile.pronouns})
                            </span>
                          )}
                          <br />
                        </div>
                        <ul className="stick-list">
                          {artist.address?.state && (
                            <li>
                              <LocationIcon aria-label="Location" />
                              {`${artist.address.state} ${artist.address.zip}`}
                            </li>
                          )}
                          {showPhoneOnMap && artist.detail?.phone && (
                            <li>
                              <PhoneIcon aria-label="Phone" />
                              <Link to={`tel:${artist.detail.phone}`}>
                                {artist.detail.phone}
                              </Link>
                            </li>
                          )}
                          {artist.detail?.website && (
                            <li>
                              <DesktopIcon aria-label="Website" />
                              <ExternalLink href={artist.detail.website}>
                                Website
                              </ExternalLink>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="options">
                    <ActiveBadges badges={get(artist, 'badges')} size={40} />
                    {artist.email && (
                      <>
                        <SearchResultContactLink
                          email={artist.email || ''}
                          resultName={artist.name}
                          contactName={artist.firstName}
                        >
                          <button className="button-secondary">
                            Contact Teaching Artist
                          </button>
                        </SearchResultContactLink>
                        {showMostRecentLogin && mostRecentLoginLabel && (
                          <p className="profile-complete-badge__login-text">
                            <strong>Most Recent Login:</strong>{' '}
                            {mostRecentLoginLabel}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="header-details">
                  <div className="text-block">
                    {artist.description && <p>{artist.description}</p>}
                    <ul className="stick-list">
                      <li>
                        <ul className="align-between">
                          {artist.socialMedia && (
                            <li>
                              <InstagramIcon aria-label="Instagram" />
                              <ExternalLink href={artist.socialMedia}>
                                Instagram
                              </ExternalLink>
                            </li>
                          )}
                          {artist.resume && (
                            <li>
                              <TextIcon aria-label="Resume" />
                              <ExternalLink href={artist.resume}>
                                Resume
                              </ExternalLink>
                            </li>
                          )}
                          {artist.portfolio && (
                            <li>
                              <PaletteIcon aria-label="Portfolio" />
                              <ExternalLink href={artist.portfolio}>
                                Portfolio
                              </ExternalLink>
                            </li>
                          )}
                        </ul>
                      </li>
                      <li>
                        <ul className="align-between">
                          {profile.yearsOfExperience && (
                            <li>
                              <CalendarIcon aria-label="Experience" />
                              <span>
                                {profile.yearsOfExperience} years of experience
                              </span>
                            </li>
                          )}
                          {artist.idealWage && (
                            <li>
                              <WalletIcon aria-label="Rate" />
                              <span>
                                Ideal Wage Rate:{' '}
                                <strong>${artist.idealWage} </strong>per hour
                              </span>
                            </li>
                          )}
                        </ul>
                      </li>
                    </ul>
                  </div>
                  {artistLocation && (
                    <div className="map-block">
                      {artistLocation && (
                        <ProfileMapDisplay
                          location={artistLocation}
                          center={{
                            lat: artistLocation.latitude,
                            lng: artistLocation.longitude,
                          }}
                          zoom={10}
                          hidePopup
                        />
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </Element>
      </header>

      <ArtistContent
        artist={artist}
        isLoading={isLoading}
        artistLocation={artistLocation}
        organizationLocations={organizationLocations}
        disciplines={disciplines}
        programTypes={programTypes}
        outcomeTypes={outcomeTypes}
        approaches={approaches}
        languages={languages}
      />
    </div>
  )
}

TeachingArtist.propTypes = propTypes
TeachingArtist.defaultPropTypes = defaultPropTypes

function mapStateToProps(state) {
  return {
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    disciplines: globalSelectors.definedDisciplines(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    approaches: globalSelectors.approaches(state),
    languages: globalSelectors.languages(state),
    artist: selectors.artist(state),
    organizationLocations: selectors.organizationLocations(state),
    artistLocation: selectors.currentArtistLocation(state),
    schoolYears: selectors.orderedSchoolYears(state),
    isLoading: apiSelectors.isLoading(state, apiActions.fetchArtist),
  }
}

function mapDispatchToProps(dispatch, { params: { id } }) {
  return {
    clearArtist: () => dispatch(actions.clearArtist()),
    fetchArtist: (schoolYearId) =>
      dispatch(apiActions.fetchArtist(id, schoolYearId)),
    redirectToNotFound: () => dispatch(routerActions.replace('/not-found')),
    fetchActiveAndPublishedSchoolYears: () =>
      dispatch(apiActions.fetchActiveAndPublishedSchoolYears({ artistId: id })),
  }
}

export default compose(
  connectQuery('year'),
  connect(mapStateToProps, mapDispatchToProps)
)(TeachingArtist)
